if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      static loadDefaultLanguages() {
        if (!LocalizationForm.defaultLanguagesRequest) {
          LocalizationForm.defaultLanguagesRequest = fetch('/api/2026-10/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query: 'query OrangeCountryDefaultLocales { localization { availableCountries { isoCode defaultLanguage { isoCode } } } }',
            }),
          })
            .then((response) => {
              if (!response.ok) throw new Error('Storefront localization unavailable');
              return response.json();
            })
            .then((response) => {
              if (response.errors?.length || !response.data?.localization?.availableCountries) return {};
              return Object.fromEntries(
                response.data.localization.availableCountries.map((country) => [
                  country.isoCode,
                  country.defaultLanguage?.isoCode,
                ]),
              );
            })
            .catch(() => ({}));
        }
        return LocalizationForm.defaultLanguagesRequest;
      }

      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
        this.onDocumentPointerDown = (event) => {
          if (!this.isCompactCountry() || this.elements.button.getAttribute('aria-expanded') !== 'true') return;
          // Treat any tap outside the actual trigger and list as an outside tap.
          // The overlay is inside <localization-form>, so this.contains() alone misses it.
          const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
          const clickedTrigger = this.elements.button.contains(event.target) || path.includes(this.elements.button);
          const clickedList = this.elements.panel?.contains(event.target) || path.includes(this.elements.panel);
          if (!clickedTrigger && !clickedList) {
            this.hidePanel();
            // DC_COUNTRY_OUTSIDE_TAP_GUARD_V1: the tap that closes the menu must not also
            // activate what is underneath it (e.g. opening a product image lightbox).
            this.swallowNextClick();
          }
        };
        this.swallowNextClick = () => {
          const swallow = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            clickEvent.stopImmediatePropagation();
            release();
          };
          const release = () => {
            document.removeEventListener('click', swallow, true);
            document.removeEventListener('pointercancel', release, true);
            window.clearTimeout(timer);
          };
          // A scroll/drag gesture fires pointercancel and no click; release the guard then.
          const timer = window.setTimeout(release, 700);
          document.addEventListener('click', swallow, true);
          document.addEventListener('pointercancel', release, true);
        };
      }

      connectedCallback() {
        document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      }

      isCompactCountry() {
        return this.elements.button.classList.contains('orange-country-flag-trigger') ||
          (this.elements.button.classList.contains('orange-country-flag-mobile') && !this.mql.matches);
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        if (!this.isCompactCountry()) {
          document.body.classList.remove('overflow-hidden-mobile');
          document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        }
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        // Let the native Space-to-click behavior toggle the Orange flag once.
        if (event.code.toUpperCase() === 'SPACE' &&
            event.target === this.elements.button &&
            this.isCompactCountry()) return;
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      async onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        const countryCode = event.currentTarget.dataset.value;
        const availableLocales = (event.currentTarget.dataset.languages || '').split(',').filter(Boolean);
        const fallbackLocale = event.currentTarget.dataset.language;
        this.elements.input.value = countryCode;
        const languageField = form?.querySelector('input[name="language_code"]');
        if (languageField) {
          const defaultLanguages = await LocalizationForm.loadDefaultLanguages();
          const shopifyLocale = defaultLanguages[countryCode];
          const selectedLanguage = availableLocales.find(
            (locale) => locale.replace(/-/g, '_').toUpperCase() === shopifyLocale,
          ) || fallbackLocale;
          if (selectedLanguage) {
            languageField.value = selectedLanguage;
          } else {
            languageField.disabled = true;
          }
        }
        if (form) form.submit();
      }

      openSelector() {
        if (this.querySelector('input[name="language_code"]')) LocalizationForm.loadDefaultLanguages();
        const isCompactCountry = this.isCompactCountry();
        if (!this.elements.panel.hasAttribute('hidden')) {
          this.hidePanel();
          return;
        }
        this.elements.button.focus();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        if (!isCompactCountry && !document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches && !isCompactCountry) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        if (!isCompactCountry) {
          document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
        }
      }

      closeSelector(event) {
        // The compact flag menu is closed by the trigger click or an outside
        // pointerdown. Focusout on mobile can fire before the click and reopen it.
        if (this.isCompactCountry()) return;
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
